import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import HorizontalSeparator from "./HorizontalSeparator";
import Icon from "./Icon";
import styles from "./VideoPlayer.module.css";

interface VideoPlayerRewindProps {
    src: string;
    /** When true the video starts playing as soon as it mounts */
    isAutoPlay?: boolean;
    /** Called when playback reaches the end of the video */
    onEnded?: () => void;
    hideSeparator?: boolean;
    className?: string;
    /** Compact footer embed — no outer padding / transparent shell */
    embedded?: boolean;
}

const formatTime = (s: number) => {
    if (!isFinite(s) || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

const VideoPlayerRewind: React.FC<VideoPlayerRewindProps> = ({
    src,
    isAutoPlay = false,
    onEnded,
    hideSeparator = false,
    className = "",
    embedded = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const progressBarRef = useRef<HTMLDivElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [videoExpanded, setVideoExpanded] = useState(true);

    // Reset & optionally autoplay when source changes
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.currentTime = 0;
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);

        if (isAutoPlay) {
            v.play()
                .then(() => setIsPlaying(true))
                .catch(() => {});
        }
    }, [src, isAutoPlay]);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play();
            setIsPlaying(true);
        } else {
            v.pause();
            setIsPlaying(false);
        }
    };

    const handleTimeUpdate = () => {
        const v = videoRef.current;
        if (!v || !v.duration) return;
        setCurrentTime(v.currentTime);
        setProgress((v.currentTime / v.duration) * 100);
    };

    const handleLoadedMetadata = () => {
        const v = videoRef.current;
        if (v && isFinite(v.duration)) setDuration(v.duration);
    };

    const handleEnded = () => {
        setIsPlaying(false);
        onEnded?.();
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const v = videoRef.current;
        const bar = progressBarRef.current;
        if (!v || !v.duration || !bar) return;
        const rect = bar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = Math.max(0, Math.min(x / rect.width, 1));
        v.currentTime = pct * v.duration;
    };

    return (
        <div
            className={[styles.container, embedded || hideSeparator ? styles.containerEmbedded : "", className]
                .filter(Boolean)
                .join(" ")}
            data-rw-control="video-player"
        >
            {!hideSeparator && <HorizontalSeparator />}

            <button type="button" className={styles.toggle} onClick={() => setVideoExpanded((v) => !v)}>
                <span
                    className={`${styles.toggleChevron} ${videoExpanded ? styles.toggleChevronOpen : ""}`}
                    aria-hidden
                >
                    ▶
                </span>
                <span>{videoExpanded ? t("hidePreview") : t("showPreview")}</span>
            </button>

            {/* Always in DOM for playback; collapsed via max-height */}
            <video
                ref={videoRef}
                src={src}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                onClick={togglePlay}
                className={`${styles.video} ${videoExpanded ? styles.videoExpanded : ""}`}
            />

            <div className={styles.controls}>
                <button
                    type="button"
                    className={styles.playButton}
                    onClick={togglePlay}
                    aria-label={isPlaying ? "Pause" : "Play"}
                >
                    {isPlaying ? (
                        <Icon name="ic-pause" className={`${styles.playIcon}`} />
                    ) : (
                        <Icon name="ic-play" className={`${styles.playIcon} ${styles.playIconPlay}`} />
                    )}
                </button>

                <div className={styles.progressSection}>
                    <div
                        ref={progressBarRef}
                        className={styles.progressBar}
                        onClick={handleProgressClick}
                        role="slider"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(progress)}
                        tabIndex={0}
                    >
                        <div className={styles.progressFilled} style={{ width: `${progress}%` }} />
                        <div className={styles.progressThumb} style={{ left: `${progress}%` }} />
                    </div>
                    <div className={styles.times}>
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayerRewind;
